import { useCallback, useEffect, useRef, useState, type ChangeEvent } from "react";
import { api, ApiError, type SelfieRecord } from "../lib/api";
import { compressSelfie, selfieDataUrl } from "../lib/image";
import { Alert, Button, ConfirmDialog, Modal, Spinner } from "./ui";

export function SelfiePanel({
  code,
  token,
  isWinner,
  available,
  onAvailabilityChange
}: {
  code: string;
  token: string;
  isWinner: boolean;
  available: boolean;
  onAvailabilityChange: (available: boolean) => void;
}) {
  const [selfie, setSelfie] = useState<SelfieRecord | null>(null);
  const [loadingExisting, setLoadingExisting] = useState(available);
  const [captureOpen, setCaptureOpen] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [processed, setProcessed] = useState<SelfieRecord | null>(null);
  const [processing, setProcessing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const stopCamera = useCallback(() => {
    setStream((current) => {
      current?.getTracks().forEach((track) => track.stop());
      return null;
    });
    setCameraActive(false);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (video && stream) {
      video.srcObject = stream;
      void video.play().catch(() => undefined);
    }
  }, [stream, cameraActive]);

  useEffect(() => () => stopCamera(), [stopCamera]);

  useEffect(() => {
    if (!available) {
      setSelfie(null);
      setLoadingExisting(false);
      return;
    }
    const controller = new AbortController();
    setLoadingExisting(true);
    api
      .getSelfie(code, token, controller.signal)
      .then(setSelfie)
      .catch((caught) => {
        if (!(caught instanceof DOMException && caught.name === "AbortError")) {
          setError(
            caught instanceof ApiError ? caught.message : "Could not load the winner photo."
          );
        }
      })
      .finally(() => setLoadingExisting(false));
    return () => controller.abort();
  }, [available, code, token]);

  const openCapture = () => {
    setError(null);
    setProcessed(null);
    setCaptureOpen(true);
  };

  const closeCapture = useCallback(() => {
    stopCamera();
    setCaptureOpen(false);
    setProcessed(null);
    setError(null);
  }, [stopCamera]);

  const startCamera = async () => {
    setError(null);
    if (!navigator.mediaDevices?.getUserMedia) {
      setError("Camera access is unavailable here. Choose a photo file instead.");
      fileRef.current?.click();
      return;
    }
    try {
      const nextStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 960 }, height: { ideal: 960 } },
        audio: false
      });
      setStream(nextStream);
      setCameraActive(true);
    } catch {
      setError("Camera permission was not granted. You can still choose a photo file.");
    }
  };

  const processFile = async (file: File) => {
    setProcessing(true);
    setError(null);
    try {
      setProcessed(await compressSelfie(file));
      stopCamera();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not process that photo.");
    } finally {
      setProcessing(false);
    }
  };

  const chooseFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file) void processFile(file);
  };

  const captureFrame = async () => {
    const video = videoRef.current;
    if (!video || video.videoWidth === 0 || video.videoHeight === 0) {
      setError("The camera is still starting. Try again in a moment.");
      return;
    }
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext("2d", { alpha: false });
    if (!context) return;
    context.translate(canvas.width, 0);
    context.scale(-1, 1);
    context.drawImage(video, 0, 0);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 0.9)
    );
    if (!blob) {
      setError("Could not capture that frame. Choose a photo file instead.");
      return;
    }
    await processFile(new File([blob], "winner-selfie.jpg", { type: "image/jpeg" }));
  };

  const upload = async () => {
    if (!processed) return;
    setUploading(true);
    setError(null);
    try {
      const saved = await api.saveSelfie(code, token, processed);
      setSelfie(saved);
      onAvailabilityChange(true);
      closeCapture();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Could not save the photo. Your game is still complete."
      );
    } finally {
      setUploading(false);
    }
  };

  const remove = async () => {
    setDeleting(true);
    setError(null);
    try {
      await api.deleteSelfie(code, token);
      setSelfie(null);
      onAvailabilityChange(false);
      setConfirmDelete(false);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not delete the photo.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <section className="selfie-panel" aria-labelledby="selfie-title" data-testid="selfie-panel">
      <div className="selfie-panel__copy">
        <p className="eyebrow">VICTORY PROOF / OPTIONAL</p>
        <h2 id="selfie-title">Winner selfie</h2>
        <p>
          The saved photo belongs to this private game and is visible to both players. It is
          optional and never blocks a rematch.
        </p>
        {error && <Alert tone="error">{error}</Alert>}
        {loadingExisting && <Spinner label="Loading photo…" />}
        {selfie && (
          <div className="selfie-panel__actions">
            {isWinner && (
              <>
                <Button onClick={openCapture} data-testid="replace-selfie">
                  Replace photo
                </Button>
                <Button
                  variant="danger"
                  onClick={() => setConfirmDelete(true)}
                  data-testid="delete-selfie"
                >
                  Delete photo
                </Button>
              </>
            )}
          </div>
        )}
        {!selfie && !loadingExisting && isWinner && (
          <Button variant="primary" onClick={openCapture} data-testid="take-selfie">
            Take winner selfie →
          </Button>
        )}
        {!selfie && !loadingExisting && !isWinner && (
          <p className="empty-copy">The winner has not saved a photo.</p>
        )}
      </div>
      <div className={`selfie-frame ${selfie ? "has-photo" : ""}`}>
        {selfie ? (
          <img
            src={selfieDataUrl(selfie)}
            alt="Winner’s saved victory selfie"
            width={selfie.width}
            height={selfie.height}
          />
        ) : (
          <div aria-hidden="true">
            <span>WIN</span>
            <b>!</b>
          </div>
        )}
      </div>

      {captureOpen && (
        <Modal
          title={selfie ? "Replace winner selfie" : "Take winner selfie"}
          description="Nothing is uploaded until you press Save photo. Processing happens on this device and strips image metadata."
          onClose={closeCapture}
          wide
        >
          <div className="capture-flow">
            <Alert tone="warning">
              By saving, you agree this photo will be stored with the private game and visible to
              both players.
            </Alert>
            <input
              ref={fileRef}
              className="visually-hidden"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={chooseFile}
            />

            {processed ? (
              <div className="capture-preview">
                <img
                  src={selfieDataUrl(processed)}
                  alt="Processed selfie preview"
                  width={processed.width}
                  height={processed.height}
                />
                <p>
                  {processed.width} × {processed.height} · metadata removed
                </p>
              </div>
            ) : cameraActive ? (
              <div className="camera-preview">
                <video ref={videoRef} muted playsInline aria-label="Live camera preview" />
              </div>
            ) : (
              <div className="camera-placeholder">
                <span aria-hidden="true">◎</span>
                <p>Camera access starts only when you ask.</p>
              </div>
            )}

            {error && <Alert tone="error">{error}</Alert>}
            {processing && <Spinner label="Compressing and removing metadata…" />}

            <div className="capture-actions">
              {!processed && !cameraActive && (
                <Button
                  variant="primary"
                  onClick={() => void startCamera()}
                  disabled={processing}
                  data-testid="start-camera"
                >
                  Use camera
                </Button>
              )}
              {!processed && cameraActive && (
                <Button
                  variant="primary"
                  onClick={() => void captureFrame()}
                  disabled={processing}
                  data-testid="capture-selfie"
                >
                  Capture
                </Button>
              )}
              {!processed && (
                <Button
                  onClick={() => fileRef.current?.click()}
                  disabled={processing}
                  data-testid="pick-selfie-file"
                >
                  Choose photo file
                </Button>
              )}
              {processed && (
                <>
                  <Button
                    onClick={() => setProcessed(null)}
                    disabled={uploading}
                    data-testid="retake-selfie"
                  >
                    Retake
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => void upload()}
                    disabled={uploading}
                    data-testid="upload-selfie"
                  >
                    {uploading ? "Saving…" : "Save photo"}
                  </Button>
                </>
              )}
            </div>
          </div>
        </Modal>
      )}

      {confirmDelete && (
        <ConfirmDialog
          title="Delete winner selfie?"
          description="This permanently removes the saved photo from the game. You can take a new one later."
          confirmLabel="Delete"
          danger
          busy={deleting}
          onConfirm={remove}
          onClose={() => setConfirmDelete(false)}
        />
      )}
    </section>
  );
}
